import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, ArrowBack, ArrowForward, Close, Delete, ZoomIn } from '@mui/icons-material'
import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { BLUEPRINT_FAMILY_MAX_EXAMPLE_IMAGES, BlueprintFamilyExampleImage, resolveExampleImageGameName } from '../../../api/blueprintFamilies/exampleImages'
import { GRAccordion } from '../../../components/GRAccordion/GRAccordion'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { useCurrentUserLanguage } from '../../../features/account/hooks/useCurrentUserLanguage'
import { GameIcon } from '../../../features/game'
import { borderRadiusDefault } from '../../../themes/theme'
import { useGetTrackedGamesQuery } from '../../api/internal'
import { BlueprintFamilyImagePicker } from '../BlueprintFamilyImagePicker/BlueprintFamilyImagePicker'

type BlueprintFamilyImagesEditorProps = {
  images: BlueprintFamilyExampleImage[]
  onChange: (images: BlueprintFamilyExampleImage[]) => void
  disabled?: boolean
}

// Portrait proportions, since the screenshots these hold are phone captures.
const THUMBNAIL_WIDTH = 130
const THUMBNAIL_HEIGHT = 170

/**
 * Collapsible form section for the ordered set of example images shown on a
 * Blueprint Family, both on its Data Glossary card and on its Live Event
 * Analytics page.
 *
 * Images are not uploaded: each one is a screenshot picked off a tracked live event
 * (see `BlueprintFamilyImagePicker`), so the customer can be told which game and
 * event it came from and can click through to that event.
 *
 * There is nothing here for a screenshot that has gone from its event: the BE drops
 * the family's reference the moment the shot is removed, so such an entry never
 * arrives to be flagged.
 *
 * Changes are pushed up via `onChange` so the parent edit form can persist them
 * alongside the rest of the blueprint payload on Save.
 */
export const BlueprintFamilyImagesEditor: FC<BlueprintFamilyImagesEditorProps> = ({ images, onChange, disabled = false }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Left undefined until an analyst opens or closes the section themselves, so a
  // family that already has images can show them straight away — the form only
  // fills in its values once the blueprint request resolves, after first render.
  const [expandedOverride, setExpandedOverride] = useState<boolean | undefined>(undefined)
  const expanded = expandedOverride ?? images.length > 0

  // Images store the source game's id but not its icon, so the icons come from the
  // tracked-games list. It is one request, shared with the picker's own game step
  // through the RTK Query cache, and skipped entirely while there is nothing to
  // caption.
  const { data: trackedGames } = useGetTrackedGamesQuery(undefined, { skip: images.length === 0 })
  const gamesById = useMemo(() => new Map((trackedGames || []).map((trackedGame) => [trackedGame.game.id, trackedGame.game])), [trackedGames])

  const handleAdd = useCallback(
    (image: BlueprintFamilyExampleImage) => {
      onChange([...images, image])
    },
    [images, onChange]
  )

  const handleRemove = useCallback(
    (index: number) => {
      const next = images.slice()
      next.splice(index, 1)
      onChange(next)
    },
    [images, onChange]
  )

  const handleMove = useCallback(
    (index: number, direction: -1 | 1) => {
      const target = index + direction
      if (target < 0 || target >= images.length) return
      const next = images.slice()
      ;[next[index], next[target]] = [next[target], next[index]]
      onChange(next)
    },
    [images, onChange]
  )

  const handleTileKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      action()
    }
  }, [])

  const openPicker = useCallback(() => setPickerOpen(true), [])

  // The BE rejects the eleventh with a 400; stopping here saves the analyst picking
  // a shot only to lose it on Save.
  const isFull = images.length >= BLUEPRINT_FAMILY_MAX_EXAMPLE_IMAGES
  const canAdd = !disabled && !isFull

  return (
    <>
      <GRAccordion
        title={t('internal-live-events:blueprint_images_editor_title', { total: images.length })}
        expanded={expanded}
        onChange={() => setExpandedOverride(!expanded)}
      >
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('internal-live-events:blueprint_images_editor_subtitle', { max: BLUEPRINT_FAMILY_MAX_EXAMPLE_IMAGES })}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {images.map((image, index) => {
              const sourceGame = gamesById.get(image.gameId)
              // The BE's per-market names first; the tracked-games list is the
              // fallback for a game the BE has no names on record for.
              const gameName = resolveExampleImageGameName(image, userLanguage) || sourceGame?.resolvedName

              return (
                <Stack key={`${image.eventId}-${image.url}`} spacing={0.75} sx={{ width: THUMBNAIL_WIDTH }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: THUMBNAIL_WIDTH,
                      height: THUMBNAIL_HEIGHT,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      bgcolor: 'grey.100',
                      // Hover/focus reveal the zoom-in cue and the reorder/remove
                      // controls, so a row of thumbnails reads as the screenshots
                      // themselves rather than as a wall of buttons. Keyboard focus
                      // mirrors hover so the affordances stay reachable; the controls
                      // use focus-within because the focus lands on them, not the tile.
                      '&:hover .blueprint-image-zoom-cue, & .blueprint-image-preview:focus-visible .blueprint-image-zoom-cue': {
                        opacity: 1,
                      },
                      '&:hover .blueprint-image-actions, &:focus-within .blueprint-image-actions': {
                        opacity: 1,
                      },
                    }}
                  >
                    {/* The preview is a button of its own, beside the toolbar rather than around it, so the
                        reorder and remove buttons stay separate controls for assistive technology. */}
                    <Box
                      className="blueprint-image-preview"
                      role="button"
                      tabIndex={0}
                      aria-label={t('internal-live-events:blueprint_image_preview', { index: index + 1 })}
                      onClick={() => setPreviewUrl(image.url)}
                      onKeyDown={(event) => handleTileKeyDown(event, () => setPreviewUrl(image.url))}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        cursor: 'pointer',
                        '&:focus-visible': {
                          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: -2,
                          borderRadius: 2,
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={image.url}
                        alt={t('internal-live-events:blueprint_image_alt', { index: index + 1 })}
                        loading="lazy"
                        decoding="async"
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      <Box
                        className="blueprint-image-zoom-cue"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: (theme) => alpha(theme.palette.common.black, 0.35),
                          color: 'common.white',
                          opacity: 0,
                          transition: 'opacity 150ms ease-in-out',
                          // The overlay is purely visual feedback for the preview button's
                          // click handler, so the click lands on the button through it.
                          pointerEvents: 'none',
                        }}
                      >
                        <ZoomIn sx={{ fontSize: 32 }} />
                      </Box>
                    </Box>
                    <Stack
                      className="blueprint-image-actions"
                      direction="row"
                      spacing={0.5}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                        // GR design system default radius. On a 40px-tall pill this
                        // resolves to a full capsule, matching the rounded language
                        // the rest of the app uses (Buttons, Cards, Inputs, Alerts).
                        borderRadius: borderRadiusDefault,
                        p: 0.25,
                        opacity: 0,
                        transition: 'opacity 150ms ease-in-out',
                        // Lift the icon buttons to a 32px hit area without going up to
                        // size="medium" (40px), which would crowd a 130px thumbnail when
                        // three controls sit side by side.
                        '& .MuiIconButton-root': { width: 32, height: 32 },
                      }}
                    >
                      {index > 0 && (
                        <GRTooltip content={t('internal-live-events:blueprint_image_move_left')}>
                          <IconButton
                            size="small"
                            aria-label={t('internal-live-events:blueprint_image_move_left')}
                            disabled={disabled}
                            onClick={() => handleMove(index, -1)}
                          >
                            <ArrowBack fontSize="small" />
                          </IconButton>
                        </GRTooltip>
                      )}
                      {index < images.length - 1 && (
                        <GRTooltip content={t('internal-live-events:blueprint_image_move_right')}>
                          <IconButton
                            size="small"
                            aria-label={t('internal-live-events:blueprint_image_move_right')}
                            disabled={disabled}
                            onClick={() => handleMove(index, 1)}
                          >
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </GRTooltip>
                      )}
                      <GRTooltip content={t('internal-live-events:blueprint_image_remove')}>
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label={t('internal-live-events:blueprint_image_remove')}
                          disabled={disabled}
                          onClick={() => handleRemove(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </GRTooltip>
                    </Stack>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 6,
                        left: 6,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // GR primary-chip vocabulary: orchid fill, white bold text. Reads
                        // the position number as brand metadata rather than a neutral badge.
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                        {index + 1}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                      {sourceGame && (
                        <GameIcon src={sourceGame.getIcon() as string} gameName={sourceGame.resolvedName} size="xtiny" rootStyle={{ flexShrink: 0 }} />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" component="div" noWrap sx={{ fontWeight: 'bold' }} title={gameName}>
                          {gameName || t('internal-live-events:blueprint_image_source_unknown_game')}
                        </Typography>
                        <Typography variant="caption" component="div" color="text.secondary" noWrap title={image.eventName}>
                          {image.eventName}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              )
            })}

            <GRTooltip
              content={isFull ? t('internal-live-events:blueprint_images_max_reached', { max: BLUEPRINT_FAMILY_MAX_EXAMPLE_IMAGES }) : ''}
              tooltipProps={{ disableHoverListener: !isFull, disableFocusListener: !isFull }}
            >
              <Box
                role="button"
                tabIndex={canAdd ? 0 : -1}
                aria-label={t('internal-live-events:blueprint_images_add_from_event')}
                aria-disabled={!canAdd}
                onClick={canAdd ? openPicker : undefined}
                onKeyDown={(event) => canAdd && handleTileKeyDown(event, openPicker)}
                sx={{
                  width: THUMBNAIL_WIDTH,
                  height: THUMBNAIL_HEIGHT,
                  borderRadius: 2,
                  border: (theme) => `1px dashed ${theme.palette.primary.main}`,
                  color: 'primary.main',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: canAdd ? 'pointer' : 'default',
                  opacity: canAdd ? 1 : 0.5,
                  '&:hover': {
                    bgcolor: (theme) => (canAdd ? alpha(theme.palette.primary.main, 0.06) : 'transparent'),
                  },
                  '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Add />
                <Typography variant="caption">{t('internal-live-events:blueprint_images_add_from_event')}</Typography>
              </Box>
            </GRTooltip>
          </Box>
        </Box>
      </GRAccordion>

      <BlueprintFamilyImagePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={handleAdd} selectedImages={images} />

      <Dialog
        open={previewUrl !== null}
        onClose={() => setPreviewUrl(null)}
        maxWidth={false}
        // Tighten the paper to the image dimensions so the dialog doesn't show
        // dead chrome around the bitmap; the image itself sets the visible bounds.
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
      >
        <Box sx={{ position: 'relative', lineHeight: 0 }}>
          <IconButton
            onClick={() => setPreviewUrl(null)}
            aria-label={t('internal-live-events:blueprint_image_preview_close')}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.background.paper, 1),
              },
            }}
          >
            <Close />
          </IconButton>
          {previewUrl && (
            <Box
              component="img"
              src={previewUrl}
              alt={t('internal-live-events:blueprint_image_preview_alt')}
              sx={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: borderRadiusDefault,
              }}
            />
          )}
        </Box>
      </Dialog>
    </>
  )
}
